import { useAccount, useMsal } from "@azure/msal-react";
import { getAccessToken } from "../auth/authFunctions";
import { useCallback, useEffect, useState } from "react";
import {
    UserType,
    FileMetadata,
    Account,
    FileType,
    FileUpdateType,
    DocFamilyOptions,
    AccountSearchResult,
    UploadLog,
    UploadLogLoad,
    ReferenceMapping,
    ReferenceMappingInput,
    SupplierMapping,
    SupplierMappingInput,
    SupplierSearchResult,
    OpenPassword,
    DocDefKeyId,
    DocDefKeyIdSummary,
    DocDefParse,
    FppProcess,
    FppRuleGroup,
    FppCondition,
    ReportOption,
    ReportParam,
    ReportRunResult,
    ColumnDefaultsResponse,
    RescanRequestResult,
    RescanStatus,
    RemoteResource,
    RemoteResourceTest,
} from "../types";
import { MetadataGraph, RowsResponse, type NavNode } from "../schemas/crudMeta";

export type NavTreeNode = NavNode & { children: NavTreeNode[] };

export function useApi() {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [ready, setReady] = useState<boolean>(false);

    useEffect(() => {
        if (instance && account) setReady(true);
    }, [instance, account]);

    // Centralized headers and API request handling function
    const buildHeaders = useCallback(async (): Promise<Record<string, string>> => {
        if (!instance || !account) throw new Error("API not ready");

        const accessToken = await getAccessToken(instance, account);
        const headers: Record<string, string> = {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
        };

        return headers;
    }, [instance, account]);

    const fetchApi = useCallback(
        async (url: string, method: string = "GET", body?: any): Promise<any> => {
            const headers = await buildHeaders();
            const options: RequestInit = {
                method,
                headers,
                ...(body ? { body: JSON.stringify(body) } : {}),
            };

            const response = await fetch(`${import.meta.env.VITE_API_URL}${url}`, options);

            if (!response.ok) {
                // Preserve the JSON error body (e.g. Generic CRUD's 409 conflict payload with
                // id/row_version, or a 400's Zod validation issues) on the thrown error as
                // `.body`, in addition to the existing plain-text `.message` every other caller
                // already relies on - this is purely additive, nothing existing reads `.body`.
                let parsedBody: any = null;
                try {
                    parsedBody = await response.clone().json();
                } catch {
                    // response wasn't JSON - fall through with parsedBody left null
                }
                const err = new Error(parsedBody?.error || `Error: ${response.statusText}`) as Error & { status?: number; body?: any };
                err.status = response.status;
                err.body = parsedBody;
                throw err;
            }

            return response.json();
        }, [buildHeaders]);

    const hasAccounts = useCallback(async (): Promise<boolean> => {
        const data = await fetchApi("/has_accounts");
        return data as boolean;
    }, [fetchApi]);

    const getRecentFiles = useCallback(async (): Promise<FileMetadata[]> => {
        const data = await fetchApi(`/files/recent_files`);
        return data as FileMetadata[];
    }, [fetchApi]);

    const getCurrentUser = useCallback(async (): Promise<UserType> => {
        const data = await fetchApi("/users/current_user");
        return data as UserType;
    }, [fetchApi]);

    const getAccounts = useCallback(async (): Promise<Account[]> => {
        const data = await fetchApi("/accounts");
        return data as Account[];
    }, [fetchApi]);

    const updateFiles = useCallback(async (files: FileType[] | FileUpdateType[]): Promise<boolean> => {
        const data = await fetchApi("/files/update", "PUT", { files });
        return data as boolean;
    }, [fetchApi]);

    const getFiles = useCallback(async (showDays: number | string = 30): Promise<FileType[]> => {
        const data = await fetchApi(`/files?showDays=${encodeURIComponent(showDays)}`);
        return data as FileType[];
    }, [fetchApi]);

    const getFile = useCallback(async (fileId: string): Promise<FileType> => {
        const data = await fetchApi(`/file?fileId=${fileId}`);
        return data as FileType;
    }, [fetchApi]);

    const downloadFile = useCallback(async (drive: string, dir: string, file: string): Promise<Uint8Array> => {
        const data = await fetchApi("/files/download", "POST", { drive, dir, file });
        return new Uint8Array(data.data);
    }, [fetchApi]);

    // One destination, the edb_megaloader input folder -- no upload type since 2026-09-16.
    const uploadFile = useCallback(async (fileName: string, byteArray: number[], creationTime: string, lastWriteTime: string): Promise<boolean> => {
        const data = await fetchApi("/files/upload", "POST", { fileName, byteArray, creationTime, lastWriteTime });
        return data as boolean;
    }, [fetchApi]);

    const getDocFamilyOptions = useCallback(async (): Promise<DocFamilyOptions[]> => {
        const data = await fetchApi("/files/doc_family_options");
        return data.map((option: { doc_family: string; selection: string, description: string }) => ({
            value: option.doc_family,
            label: option.description
        }));
    }, [fetchApi]);

    const getDocFromOptions = useCallback(async (): Promise<any[]> => {
        const data = await fetchApi("/files/doc_from_options");
        // main.doc_from_vw builds short_code as 'S-'+supplier_code / 'C-'+account_code, so a
        // NULL code makes the whole column NULL. Coerce to strings here - callers treat these
        // as { value: string; label: string } and .trim() on a null blanks the page.
        return data.map((option: { short_code: string | null; name: string | null }) => ({
            value: option.short_code ?? "",
            label: option.short_code
                ? `${option.short_code} - ${option.name ?? ""}`
                : (option.name ?? "")
        }));
    }, [fetchApi]);

    const callPython = useCallback(async (): Promise<string> => {
        const data = await fetchApi("/python", "POST");
        return data as string;
    }, [fetchApi]);

    const getSuggestedAccounts = useCallback(async (searchTerm: string): Promise<AccountSearchResult[]> => {
        const data = await fetchApi(`/files/get_suggested_accounts?search=${encodeURIComponent(searchTerm)}`);
        return data as AccountSearchResult[];
    }, [fetchApi]);

    const getUploadLog = useCallback(async (): Promise<UploadLog[]> => {
        const data = await fetchApi("/files/get_upload_log");
        return data as UploadLog[];
    }, [fetchApi]);

    // the tables one Upload Log row (load session) wrote - its Sheets pop-up
    const getUploadLogLoads = useCallback(async (sk: number): Promise<UploadLogLoad[]> => {
        const data = await fetchApi(`/files/get_upload_log_loads?sk=${encodeURIComponent(sk)}`);
        return data as UploadLogLoad[];
    }, [fetchApi]);

    // Where an Upload Log row's file sits in the hash library (null = not registered in doc.file_store)
    const getDocByHash = useCallback(async (filehash: string): Promise<{ filehash: string; doc_path: string; eb_filename: string } | null> => {
        const data = await fetchApi(`/files/doc_by_hash?filehash=${encodeURIComponent(filehash)}`);
        return data as { filehash: string; doc_path: string; eb_filename: string } | null;
    }, [fetchApi]);

    const checkPermissions = useCallback(async (accessLevel: string): Promise<{ has_access: number, message: string }> => {
        const data = await fetchApi(`/check_permissions?accesslevel=${encodeURIComponent(accessLevel)}`);
        return data;
    }, [fetchApi]);

    // Mandatory login health check: proves the backend can actually reach the configured
    // database, and deliberately nothing else (no stored procedure is involved - see
    // app/src/controllers/Health.ts). Also returns the database name, so the Technical Details
    // page can show which DB the app is really pointed at.
    const checkDatabase = useCallback(async (): Promise<{ ok: boolean; dbName: string | null }> => {
        const data = await fetchApi("/login/db_check");
        return data;
    }, [fetchApi]);

    // Mandatory login health check: writes a real login-record file to every configured file
    // share, so the app can detect (and block on) a share that isn't actually reachable.
    const checkLoginFileAccess = useCallback(async (): Promise<{ results: { drive: string; ok: boolean }[] }> => {
        const data = await fetchApi("/login/file_check", "POST");
        return data;
    }, [fetchApi]);

    // Generic CRUD engine - a separate new feature (see
    // office-portal/design/CRUD_engine_*.md). Phase 1 scope: read-only.
    const getGenericCrudMeta = useCallback(async (): Promise<MetadataGraph> => {
        const data = await fetchApi("/generic_crud/meta");
        return MetadataGraph.parse(data);
    }, [fetchApi]);

    const getGenericCrudNav = useCallback(async (): Promise<NavTreeNode[]> => {
        const data = await fetchApi("/generic_crud/nav");
        return data as NavTreeNode[];
    }, [fetchApi]);

    const getGenericCrudRows = useCallback(
        async (entity: string, params: { offset?: number; limit?: number; sortColumn?: string; sortDir?: "ASC" | "DESC" } = {}): Promise<RowsResponse> => {
            const qs = new URLSearchParams();
            if (params.offset != null) qs.set("offset", String(params.offset));
            if (params.limit != null) qs.set("limit", String(params.limit));
            if (params.sortColumn) qs.set("sortColumn", params.sortColumn);
            if (params.sortDir) qs.set("sortDir", params.sortDir);
            const data = await fetchApi(`/generic_crud/${entity}/rows?${qs.toString()}`);
            return RowsResponse.parse(data);
        },
        [fetchApi],
    );

    const putGenericCrudRow = useCallback(
        async (entity: string, row: Record<string, unknown>): Promise<{ sk: number; row_version: string | null; message: string }> => {
            return fetchApi(`/generic_crud/${entity}/rows`, "PUT", row);
        },
        [fetchApi],
    );

    const deleteGenericCrudRow = useCallback(
        async (entity: string, id: number): Promise<{ message: string }> => {
            return fetchApi(`/generic_crud/${entity}/rows/${id}`, "DELETE");
        },
        [fetchApi],
    );

    const getGenericCrudChildRows = useCallback(
        async (entity: string, id: number, childEntity: string): Promise<{ rows: Record<string, unknown>[] }> => {
            return fetchApi(`/generic_crud/${entity}/${id}/child/${childEntity}`);
        },
        [fetchApi],
    );

    const linkGenericCrudChild = useCallback(
        async (entity: string, id: number, childEntity: string, childId: number): Promise<{ message: string }> => {
            return fetchApi(`/generic_crud/${entity}/${id}/child/${childEntity}/link`, "POST", { childId });
        },
        [fetchApi],
    );

    const unlinkGenericCrudChild = useCallback(
        async (entity: string, id: number, childEntity: string, childId: number): Promise<{ message: string }> => {
            return fetchApi(`/generic_crud/${entity}/${id}/child/${childEntity}/link/${childId}`, "DELETE");
        },
        [fetchApi],
    );

    const getGenericCrudRowTags = useCallback(
        async (entity: string, id: number): Promise<{ tags: { sk: number; tag_name: string }[] }> => {
            return fetchApi(`/generic_crud/${entity}/${id}/tags`);
        },
        [fetchApi],
    );

    const addGenericCrudRowTag = useCallback(
        async (entity: string, id: number, tagName: string): Promise<{ sk: number; tagName: string }> => {
            return fetchApi(`/generic_crud/${entity}/${id}/tags`, "POST", { tagName });
        },
        [fetchApi],
    );

    const removeGenericCrudRowTag = useCallback(
        async (entity: string, id: number, tagId: number): Promise<{ message: string }> => {
            return fetchApi(`/generic_crud/${entity}/${id}/tags/${tagId}`, "DELETE");
        },
        [fetchApi],
    );

    const getReferenceMappings = useCallback(async (): Promise<ReferenceMapping[]> => {
        const data = await fetchApi("/reference/crm_account_mapping");
        return data as ReferenceMapping[];
    }, [fetchApi]);

    const updateReferenceMapping = useCallback(async (mapping: ReferenceMapping): Promise<boolean> => {
        const data = await fetchApi("/reference/crm_account_mapping", "PUT", mapping);
        return data as boolean;
    }, [fetchApi]);

    const addReferenceMapping = useCallback(async (mapping: ReferenceMappingInput): Promise<boolean> => {
        const data = await fetchApi("/reference/crm_account_mapping", "POST", mapping);
        return data as boolean;
    }, [fetchApi]);

    // ref.crm_supplier_mapping - the supplier twin of the account mapping calls above. Both writes
    // hit the same proc (dbo.sp_web_grid__supplier_mapping__put): POST with no sk inserts, PUT with
    // an sk updates. Input rejections come back as 400 with the proc's own message in .message.
    const getSupplierMappings = useCallback(async (): Promise<SupplierMapping[]> => {
        const data = await fetchApi("/reference/crm_supplier_mapping");
        return data as SupplierMapping[];
    }, [fetchApi]);

    const updateSupplierMapping = useCallback(async (mapping: SupplierMappingInput & { sk: number }): Promise<{ sk: number | null }> => {
        const data = await fetchApi("/reference/crm_supplier_mapping", "PUT", mapping);
        return data as { sk: number | null };
    }, [fetchApi]);

    const addSupplierMapping = useCallback(async (mapping: SupplierMappingInput): Promise<{ sk: number | null }> => {
        const data = await fetchApi("/reference/crm_supplier_mapping", "POST", mapping);
        return data as { sk: number | null };
    }, [fetchApi]);

    const getSuggestedSuppliers = useCallback(async (searchTerm: string): Promise<SupplierSearchResult[]> => {
        const data = await fetchApi(`/reference/get_suggested_suppliers?search=${encodeURIComponent(searchTerm)}`);
        return data as SupplierSearchResult[];
    }, [fetchApi]);

    const getOpenPasswords = useCallback(async (): Promise<OpenPassword[]> => {
        const data = await fetchApi("/doc/open_password");
        return data as OpenPassword[];
    }, [fetchApi]);

    const addOpenPassword = useCallback(async (password: string): Promise<{ sk: number | null }> => {
        const data = await fetchApi("/doc/open_password", "POST", { password });
        return data as { sk: number | null };
    }, [fetchApi]);

    const getDocDefKeyIds = useCallback(async (): Promise<DocDefKeyIdSummary[]> => {
        const data = await fetchApi("/doc/def_key_id");
        return data as DocDefKeyIdSummary[];
    }, [fetchApi]);

    const putFileKeyId = useCallback(async (sid: string, key_id: string): Promise<boolean> => {
        const data = await fetchApi("/doc/file_key_id", "PUT", { sid, key_id });
        return data as boolean;
    }, [fetchApi]);

    const rescanDoc = useCallback(async (sid: string): Promise<RescanRequestResult> => {
        const data = await fetchApi("/files/rescan", "PUT", { sid });
        // an older backend answers `true`: queued, nothing filing it now
        return typeof data === "object" && data !== null
            ? (data as RescanRequestResult)
            : { queued: Boolean(data), immediate: false };
    }, [fetchApi]);

    const getRescanStatus = useCallback(async (sid: string): Promise<RescanStatus> => {
        const data = await fetchApi("/files/rescan_status", "POST", { sid });
        return data as RescanStatus;
    }, [fetchApi]);

    const putDocDefKeyId = useCallback(async (def: DocDefKeyId): Promise<boolean> => {
        const data = await fetchApi("/doc/def_key_id", "PUT", def);
        return data as boolean;
    }, [fetchApi]);

    const getDocDefParse = useCallback(async (key_id: string, key_name?: string): Promise<DocDefParse[]> => {
        const params = new URLSearchParams({ key_id });
        if (key_name) params.append("key_name", key_name);
        const data = await fetchApi(`/doc/def_parse?${params.toString()}`);
        return data as DocDefParse[];
    }, [fetchApi]);

    const putDocDefParse = useCallback(async (row: DocDefParse): Promise<boolean> => {
        const data = await fetchApi("/doc/def_parse", "PUT", row);
        return data as boolean;
    }, [fetchApi]);

    const deleteDocDefParse = useCallback(async (sk: number): Promise<boolean> => {
        const data = await fetchApi(`/doc/def_parse/${sk}`, "DELETE");
        return data as boolean;
    }, [fetchApi]);

    // Process Mapping
    const getProcesses = useCallback(async (): Promise<FppProcess[]> => {
        const data = await fetchApi("/process_mapping/processes");
        return data as FppProcess[];
    }, [fetchApi]);

    const postProcess = useCallback(async (process: Pick<FppProcess, "process_name" | "description">): Promise<boolean> => {
        const data = await fetchApi("/process_mapping/processes", "POST", process);
        return data as boolean;
    }, [fetchApi]);

    const putProcess = useCallback(async (process: FppProcess): Promise<boolean> => {
        const data = await fetchApi("/process_mapping/processes", "PUT", process);
        return data as boolean;
    }, [fetchApi]);

    const deleteProcess = useCallback(async (process_id: number): Promise<boolean> => {
        const data = await fetchApi(`/process_mapping/processes/${process_id}`, "DELETE");
        return data as boolean;
    }, [fetchApi]);

    const getRuleGroups = useCallback(async (): Promise<FppRuleGroup[]> => {
        const data = await fetchApi("/process_mapping/rule_groups");
        return data as FppRuleGroup[];
    }, [fetchApi]);

    const postRuleGroup = useCallback(async (ruleGroup: Pick<FppRuleGroup, "process_id" | "description">): Promise<boolean> => {
        const data = await fetchApi("/process_mapping/rule_groups", "POST", ruleGroup);
        return data as boolean;
    }, [fetchApi]);

    const putRuleGroup = useCallback(async (ruleGroup: Pick<FppRuleGroup, "rule_group_id" | "process_id" | "description">): Promise<boolean> => {
        const data = await fetchApi("/process_mapping/rule_groups", "PUT", ruleGroup);
        return data as boolean;
    }, [fetchApi]);

    const deleteRuleGroup = useCallback(async (rule_group_id: number): Promise<boolean> => {
        const data = await fetchApi(`/process_mapping/rule_groups/${rule_group_id}`, "DELETE");
        return data as boolean;
    }, [fetchApi]);

    const getConditions = useCallback(async (rule_group_id: number): Promise<FppCondition[]> => {
        const data = await fetchApi(`/process_mapping/conditions?rule_group_id=${rule_group_id}`);
        return data as FppCondition[];
    }, [fetchApi]);

    const postCondition = useCallback(async (condition: Omit<FppCondition, "condition_id" | "is_active">): Promise<boolean> => {
        const data = await fetchApi("/process_mapping/conditions", "POST", condition);
        return data as boolean;
    }, [fetchApi]);

    const putCondition = useCallback(async (condition: Omit<FppCondition, "rule_group_id" | "is_active">): Promise<boolean> => {
        const data = await fetchApi("/process_mapping/conditions", "PUT", condition);
        return data as boolean;
    }, [fetchApi]);

    const deleteCondition = useCallback(async (condition_id: number): Promise<boolean> => {
        const data = await fetchApi(`/process_mapping/conditions/${condition_id}`, "DELETE");
        return data as boolean;
    }, [fetchApi]);

    // Reports - standalone, no dependency on the Generic CRUD engine above.
    const getReports = useCallback(async (): Promise<ReportOption[]> => {
        const data = await fetchApi("/reports");
        return data as ReportOption[];
    }, [fetchApi]);

    const getReportParams = useCallback(async (sk: number): Promise<ReportParam[]> => {
        const data = await fetchApi(`/reports/${sk}/params`);
        return data as ReportParam[];
    }, [fetchApi]);

    const runReport = useCallback(async (sk: number, params: Record<string, string>): Promise<ReportRunResult> => {
        const data = await fetchApi(`/reports/${sk}/run`, "POST", { params });
        return data as ReportRunResult;
    }, [fetchApi]);

    // DB-driven ag-grid column display defaults - fetched once at login (ColumnDefaultsContext).
    const getColumnDefaults = useCallback(async (): Promise<ColumnDefaultsResponse> => {
        const data = await fetchApi("/gui/column_defaults");
        return data as ColumnDefaultsResponse;
    }, [fetchApi]);

    // Technical Details resource grid - the list comes from the backend's environment variables
    // (app/src/controllers/TechnicalDetails.ts); staff only, enforced by the backend too.
    const getRemoteResources = useCallback(async (): Promise<RemoteResource[]> => {
        const data = await fetchApi("/technical_details/resources");
        return data as RemoteResource[];
    }, [fetchApi]);

    const testRemoteResource = useCallback(async (id: string): Promise<RemoteResourceTest> => {
        const data = await fetchApi(`/technical_details/resources/${encodeURIComponent(id)}/test`);
        return data as RemoteResourceTest;
    }, [fetchApi]);

    return {
        ready,
        rescanDoc,
        getRescanStatus,
        getDocDefKeyIds,
        putFileKeyId,
        putDocDefKeyId,
        getDocDefParse,
        putDocDefParse,
        deleteDocDefParse,
        getGenericCrudMeta,
        getGenericCrudNav,
        getGenericCrudRows,
        putGenericCrudRow,
        deleteGenericCrudRow,
        getGenericCrudChildRows,
        linkGenericCrudChild,
        unlinkGenericCrudChild,
        getGenericCrudRowTags,
        addGenericCrudRowTag,
        removeGenericCrudRowTag,
        addReferenceMapping,
        updateReferenceMapping,
        getReferenceMappings,
        getSupplierMappings,
        addSupplierMapping,
        updateSupplierMapping,
        getSuggestedSuppliers,
        getOpenPasswords,
        addOpenPassword,
        checkPermissions,
        checkDatabase,
        checkLoginFileAccess,
        getCurrentUser,
        downloadFile,
        getRecentFiles,
        hasAccounts,
        getAccounts,
        getFiles,
        getFile,
        updateFiles,
        uploadFile,
        getDocFamilyOptions,
        getDocFromOptions,
        callPython,
        getSuggestedAccounts,
        getUploadLog,
        getUploadLogLoads,
        getDocByHash,
        getProcesses, postProcess, putProcess, deleteProcess,
        getRuleGroups, postRuleGroup, putRuleGroup, deleteRuleGroup,
        getConditions, postCondition, putCondition, deleteCondition,
        getReports, getReportParams, runReport,
        getColumnDefaults,
        getRemoteResources, testRemoteResource,
    };
}
